import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardAccountSummaryStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { AccountUtils, cn, DateUtils, PaletteUtils } from '@shared/utils';
import { PaymentItem } from './paymentItems';
import { View } from '@widgets';
import { Select, Text } from '@shared/widgets';

interface AccountsSummaryProps {
  className?: string;
}

export const AccountsSummary = (props: AccountsSummaryProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardsAccountSummaryStore = useUnit($dashboardAccountSummaryStore.store);

  const [currency, setCurrency] = useState<string>();

  const { t, i18n } = useTranslation('dashboard');

  useEffect(() => {
    $dashboardAccountSummaryStore.events.getAccountSummary();
    return () => $dashboardAccountSummaryStore.events.clear();
  }, []);

  const currentMonth = useMemo(() => DateUtils.getCurrentMonth({ locale: i18n.language }), [i18n.language]);
  const groupedAccounts = useMemo(() => {
    const filteredAccounts = AccountUtils.filterDashboardAccounts(dashboardsAccountSummaryStore.data, { currency });
    return AccountUtils.groupedDashboardAccounts(filteredAccounts);
  }, [dashboardsAccountSummaryStore.data, currency]);
  return (
    <section
      className={cn(
        'flex flex-col',
        PaletteUtils.isInsigniaPalette && 'gap-6',
        PaletteUtils.isVcfcPalette && [
          'gap-4 rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
          'max-md:p-4',
        ],
        props.className,
      )}
    >
      <div className={cn('flex justify-between', 'max-md:flex-col max-md:items-start max-md:gap-3', PaletteUtils.isVcfcPalette && 'items-center')}>
        <Text weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'normal'} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.title')}
          <Text className='ml-2' tag='span' weight='normal' size={PaletteUtils.isVcfcPalette ? 'base' : '2xl'} color='gray'>
            {currentMonth}
          </Text>
        </Text>
        <Select
          className={cn('min-w-[160px]', 'max-md:w-full')}
          nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
          items={currenciesAvailableStore.data}
          value={currency}
          onChange={setCurrency}
        />
      </div>
      <View
        loading={dashboardsAccountSummaryStore.loading}
        error={dashboardsAccountSummaryStore.error}
        noData={!groupedAccounts.length}
        onRefresh={() => $dashboardAccountSummaryStore.events.getAccountSummary()}
      >
        <div className='flex flex-col gap-6'>
          {groupedAccounts.map((group) => (
            <PaymentItem key={group.mainAccount.id} mainAccount={group.mainAccount} subAccounts={group.subAccounts} />
          ))}
        </div>
      </View>
    </section>
  );
};
